import PropTypes from "prop-types";
import React from "react";

import "./pinchZoomPan.scss";

const MIN_SCALE = 1;
const MAX_SCALE = 3;
const SETTLE_RANGE = 0.001;
const ADDITIONAL_LIMIT = 0.2;
const DOUBLE_TAP_THRESHOLD = 300;
const ANIMATION_SPEED = 0.1;
const RESET_ANIMATION_SPEED = 0.08;
const INITIAL_X = 0;
const INITIAL_Y = 0;
const INITIAL_SCALE = 1;
const MIN_DRAG_LIMIT = 5;

const settle = (val, target, range) => {
  const lowerRange = val > target - range && val < target;
  const upperRange = val < target + range && val > target;
  return lowerRange || upperRange ? target : val;
};

const inverse = x => x * -1;

const getPointFromEvent = (event, element) => {
  const rect = element.getBoundingClientRect();
  return {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top
  };
};

const getMidpoint = (pointA, pointB) => ({
  x: (pointA.x + pointB.x) / 2,
  y: (pointA.y + pointB.y) / 2
});

const getDistanceBetweenPoints = (pointA, pointB) =>
  Math.sqrt(
    Math.pow(pointA.y - pointB.y, 2) + Math.pow(pointA.x - pointB.x, 2)
  );

const between = (min, max, value) => Math.min(max, Math.max(min, value));

class PinchZoomPan extends React.Component {
  static propTypes = {
    renderContent: PropTypes.func.isRequired,
    children: PropTypes.node
  };

  constructor() {
    super(...arguments);

    this.state = {
      x: INITIAL_X,
      y: INITIAL_Y,
      scale: INITIAL_SCALE,
      limit: ADDITIONAL_LIMIT,
      dragging: false
    };
  }

  zoomTo(targetScale, midpoint) {
    cancelAnimationFrame(this.animation);

    const frame = () => {
      if (!this.container) return null;
      const { scale, limit } = this.state;

      if (scale === targetScale && limit === 0) return null;

      const distance = targetScale - scale;
      const newScale = scale + ANIMATION_SPEED * distance;

      const newLimit = limit - limit * ANIMATION_SPEED;

      this.zoom(
        settle(newScale, targetScale, SETTLE_RANGE),
        midpoint,
        settle(newLimit, 0, SETTLE_RANGE)
      );
      this.animation = requestAnimationFrame(frame);
    };

    this.animation = requestAnimationFrame(frame);
  }

  reset() {
    cancelAnimationFrame(this.animation);

    const frame = () => {
      if (
        (this.state.scale === INITIAL_SCALE &&
          this.state.x === INITIAL_X &&
          this.state.y === INITIAL_Y) ||
        !this.container
      )
        return null;
      const distance = INITIAL_SCALE - this.state.scale;
      const distanceX = INITIAL_X - this.state.x;
      const distanceY = INITIAL_Y - this.state.y;

      const targetScale = settle(
        this.state.scale + RESET_ANIMATION_SPEED * distance,
        INITIAL_SCALE,
        SETTLE_RANGE
      );
      const targetX = settle(
        this.state.x + RESET_ANIMATION_SPEED * distanceX,
        INITIAL_X,
        SETTLE_RANGE
      );
      const targetY = settle(
        this.state.y + RESET_ANIMATION_SPEED * distanceY,
        INITIAL_Y,
        SETTLE_RANGE
      );

      const nextWidth = this.container.offsetWidth * targetScale;
      const nextHeight = this.container.offsetHeight * targetScale;

      this.setState(
        {
          x: targetX,
          y: targetY,
          scale: targetScale,
          width: nextWidth,
          height: nextHeight
        },
        () => {
          this.animation = requestAnimationFrame(frame);
        }
      );
    };

    this.animation = requestAnimationFrame(frame);
  }

  handleTouchStart = event => {
    cancelAnimationFrame(this.animation);

    if (event.touches.length === 2) this.handlePinchStart(event);
    if (event.touches.length === 1) this.handleTapStart(event);

    event.preventDefault();
    event.stopPropagation();
  };

  handleTouchMove = event => {
    if (event.touches.length === 2) this.handlePinchMove(event);
    if (event.touches.length === 1) this.handlePanMove(event.touches[0]);
  };

  handleTouchEnd = event => {
    if (!this.container) return null;

    event.preventDefault();
    event.stopPropagation();

    if (event.touches.length === 1) {
      this.lastPanPoint = getPointFromEvent(event.touches[0], this.container);
    }
    if (event.touches.length > 0) return null;

    this.checkLimits();

    if (
      this.lastTouchEnd &&
      this.lastTouchEnd + DOUBLE_TAP_THRESHOLD > event.timeStamp
    ) {
      const { scale } = this.state;
      if (scale > (MIN_SCALE + MAX_SCALE) / 2) {
        this.reset();
      } else {
        return this.zoomTo(MAX_SCALE, this.lastPanPoint);
      }
    }

    this.lastTouchEnd = event.timeStamp;
  };

  checkLimits() {
    const {
      offsetWidth: containerWidth,
      offsetHeight: containerHeight
    } = this.container;
    const {
      scale,
      x,
      y,
      width = containerWidth,
      height = containerHeight
    } = this.state;

    if (scale > MAX_SCALE) {
      this.zoomTo(MAX_SCALE, this.lastMidpoint);
    } else if (scale < MIN_SCALE) {
      this.zoomTo(MIN_SCALE, this.lastMidpoint);
    } else if (
      x > 0 ||
      x < containerWidth - width ||
      y > 0 ||
      y < containerHeight - height
    ) {
      this.zoomTo(scale, this.lastMidpoint || this.lastPanPoint);
    }
  }

  handleTapStart = event => {
    if (!this.container) return null;
    this.lastPanPoint = getPointFromEvent(event.touches[0], this.container);
  };

  handlePanMove = event => {
    if (!this.container) return null;
    const {
      offsetWidth: containerWidth,
      offsetHeight: containerHeight
    } = this.container;
    const {
      scale,
      x,
      y,
      width = containerWidth,
      height = containerHeight
    } = this.state;

    if (scale === 1 || !this.lastPanPoint) return null;

    const point = getPointFromEvent(event, this.container);
    const nextX = x + point.x - this.lastPanPoint.x;
    const nextY = y + point.y - this.lastPanPoint.y;

    const limit = ADDITIONAL_LIMIT;
    this.setState({
      x: between(
        (1 - limit / 2) * containerWidth - width,
        (limit / 2) * containerWidth,
        nextX
      ),
      y: between(
        (1 - limit / 2) * containerHeight - height,
        (limit / 2) * containerHeight,
        nextY
      ),
      limit
    });

    this.lastPanPoint = point;
  };

  handlePinchStart = event => {
    if (!this.container) return null;
    this.lastPanPoint = null;
    const pointA = getPointFromEvent(event.touches[0], this.container);
    const pointB = getPointFromEvent(event.touches[1], this.container);
    this.lastDistance = getDistanceBetweenPoints(pointA, pointB);
  };

  handlePinchMove = event => {
    if (!this.container) return null;
    const pointA = getPointFromEvent(event.touches[0], this.container);
    const pointB = getPointFromEvent(event.touches[1], this.container);
    const distance = getDistanceBetweenPoints(pointA, pointB);
    const midpoint = getMidpoint(pointA, pointB);
    const scale = between(
      MIN_SCALE - ADDITIONAL_LIMIT,
      MAX_SCALE + ADDITIONAL_LIMIT,
      this.state.scale * (distance / this.lastDistance)
    );

    this.zoom(scale, midpoint);

    this.lastMidpoint = midpoint;
    this.lastDistance = distance;
  };

  handleClick = event => {
    if (!this.container) return null;
    const { scale, dragging } = this.state;

    if (dragging) {
      this.setState({ dragging: false });
      this.checkLimits();
    } else if (scale > (MIN_SCALE + MAX_SCALE) / 2) {
      this.reset();
    } else {
      const point = getPointFromEvent(event, this.container);
      return this.zoomTo(MAX_SCALE, point);
    }
  };

  handleMouseDown = event => {
    if (!this.container) return null;
    this.lastPanPoint = getPointFromEvent(event, this.container);
    event.preventDefault();
  };

  handleMouseMove = event => {
    if (!this.container) return null;
    if (event.buttons === 1) {
      const { dragging } = this.state;
      const point = getPointFromEvent(event, this.container);
      this.lastPanPoint = this.lastPanPoint || point;
      if (
        dragging ||
        getDistanceBetweenPoints(this.lastPanPoint, point) > MIN_DRAG_LIMIT
      ) {
        this.setState({ dragging: true });
        this.handlePanMove(event);
      }
    }
  };

  handleMouseLeave = () => {
    if (!this.container) return null;
    const { dragging } = this.state;

    if (dragging) {
      this.setState({ dragging: false });
      this.checkLimits();
    }
  };

  zoom(scale, midpoint, limit = ADDITIONAL_LIMIT) {
    const {
      offsetWidth: containerWidth,
      offsetHeight: containerHeight
    } = this.container;
    const {
      x,
      y,
      width = containerWidth,
      height = containerHeight
    } = this.state;

    const nextWidth = containerWidth * scale;
    const nextHeight = containerHeight * scale;
    const nextX =
      x + (inverse(midpoint.x * scale) * (nextWidth - width)) / nextWidth;
    const nextY =
      y + (inverse(midpoint.y * scale) * (nextHeight - height)) / nextHeight;

    this.setState({
      width: nextWidth,
      height: nextHeight,
      x: between(
        (1 - limit / 2) * containerWidth - nextWidth,
        (limit / 2) * containerWidth,
        nextX
      ),
      y: between(
        (1 - limit / 2) * containerHeight - nextHeight,
        (limit / 2) * containerHeight,
        nextY
      ),
      scale,
      limit
    });
  }

  render() {
    const { scale, dragging } = this.state;

    const cursor = dragging
      ? "all-scroll"
      : scale > (MIN_SCALE + MAX_SCALE) / 2
      ? "zoom-out"
      : "zoom-in";

    return (
      <div
        className="pinchZoomPanContainer"
        ref={this.setContainerRef}
        onTouchStart={this.handleTouchStart}
        onTouchMove={this.handleTouchMove}
        onTouchEnd={this.handleTouchEnd}
        onClick={this.handleClick}
        onMouseDown={this.handleMouseDown}
        onMouseMove={this.handleMouseMove}
        onMouseLeave={this.handleMouseLeave}
        style={{ cursor }}
      >
        {this.props.renderContent(this.state.x, this.state.y, this.state.scale)}
        {this.props.children}
      </div>
    );
  }

  setContainerRef = ref => {
    this.container = ref;
  };
}

export default PinchZoomPan;
